import { initAnalytics } from '@cw-elements/analytics-provider';

import { getAmplitudeConfig } from '@/libs/amplitude';
import {
  applyDatadogRumSetup,
  getDatadogRumConfig,
  initDatadogLogs,
  reportMissingDatadogCredentials
} from '@/libs/datadog';

import type { BootstrapAnalyticsConfig } from './types';

/**
 * Single bootstrap entry for Amplitude and Datadog. The analytics provider owns
 * both SDKs — the app never initializes RUM itself — so this must be awaited
 * before anything tracks: every provider function drops calls made before
 * `initAnalytics` resolves.
 *
 * Runs after LaunchDarkly so the sample rates and the consent gate are
 * flag-driven.
 */
const bootstrapAnalytics = async ({
  isConsentEnabled,
  sessionSampleRate,
  sessionReplaySampleRate
}: BootstrapAnalyticsConfig) => {
  const amplitude = getAmplitudeConfig();
  const datadog = getDatadogRumConfig({
    sessionSampleRate,
    sessionReplaySampleRate
  });

  if (datadog) {
    // A separate SDK the provider does not wrap, so the app owns this init.
    initDatadogLogs({ sessionSampleRate });
  } else {
    reportMissingDatadogCredentials();
  }

  if (!amplitude && !datadog) {
    return;
  }

  await initAnalytics({
    ...(amplitude && { amplitude }),
    ...(datadog && { datadog }),
    consent: { enabled: isConsentEnabled }
  });

  if (datadog) {
    applyDatadogRumSetup();
  }
};

export default bootstrapAnalytics;
